import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Play, Type, Sparkles, Minus, Plus, RotateCcw } from 'lucide-react'
import { DEFAULT_PREFS, loadPrefs, savePrefs } from '../utils/appPrefs'
import { toast } from '../hooks/useToast'
import './RadioPageTV.css'
import './ParametresPageTV.css'

// Défini hors du composant : sinon il serait recréé à chaque clic
// et la télécommande perdrait le focus.
const Choice = ({ on, onClick, children, initial }) => (
  <button
    type="button"
    className={`tvs-choice ${on ? 'is-on' : ''}`}
    onClick={onClick}
    aria-pressed={on}
    data-tv-initial-focus={initial ? '' : undefined}
  >
    {children}
  </button>
)

/**
 * PARAMÈTRES TV — un seul écran, gros boutons, tout se règle à la
 * télécommande. Mêmes réglages (réels) que sur téléphone.
 */
export default function ParametresPageTV({ globalAudio }) {
  const navigate = useNavigate()
  const [prefs, setPrefs] = useState(() => loadPrefs())

  const change = (patch) => setPrefs(savePrefs({ ...prefs, ...patch }))

  const setVolume = (v) => {
    const vol = Math.max(0, Math.min(100, v))
    change({ defaultVolume: vol })
    globalAudio?.setVolume?.(vol)
    if (globalAudio?.audioRef?.current && !globalAudio.isMuted) globalAudio.audioRef.current.volume = vol / 100
  }

  // Touche retour
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Backspace' || e.key === 'BrowserBack' || e.key === 'GoBack' || e.keyCode === 10009) {
        e.preventDefault()
        navigate('/')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  const reset = () => {
    setPrefs(savePrefs({ ...DEFAULT_PREFS }))
    toast.success('Réglages remis par défaut')
  }


  return (
    <div className="tvh tvs">
      <header className="tvs-head">
        <button type="button" className="tvh-btn" onClick={() => navigate('/')}>
          <ArrowLeft size="1.1em" /> Accueil
        </button>
        <h1>Paramètres</h1>
        <span className="tvs-saved">Enregistré automatiquement</span>
      </header>

      <div className="tvs-grid">
        <section className="tvs-card">
          <h2><Play size="1em" /> Lecture automatique</h2>
          <p>La radio démarre dès l'ouverture de l'application.</p>
          <div className="tvs-choices">
            <Choice on={prefs.autoplay} onClick={() => change({ autoplay: true })} initial>Oui</Choice>
            <Choice on={!prefs.autoplay} onClick={() => change({ autoplay: false })}>Non</Choice>
          </div>
        </section>

        <section className="tvs-card">
          <h2>Volume au démarrage</h2>
          <p>Volume de la radio à l'ouverture.</p>
          <div className="tvs-choices">
            <button type="button" className="tvh-btn tvh-btn--sq" onClick={() => setVolume(prefs.defaultVolume - 10)} aria-label="Moins fort">
              <Minus size="1.2em" />
            </button>
            <div className="tvh-vol tvs-vol">
              <div className="tvh-vol__bar"><i style={{ transform: `scaleX(${prefs.defaultVolume / 100})` }} /></div>
              <span>{prefs.defaultVolume}%</span>
            </div>
            <button type="button" className="tvh-btn tvh-btn--sq" onClick={() => setVolume(prefs.defaultVolume + 10)} aria-label="Plus fort">
              <Plus size="1.2em" />
            </button>
          </div>
        </section>

        <section className="tvs-card">
          <h2><Type size="1em" /> Taille du texte</h2>
          <p>Pour lire confortablement depuis le canapé.</p>
          <div className="tvs-choices">
            <Choice on={prefs.textSize === 'normal'} onClick={() => change({ textSize: 'normal' })}>Normal</Choice>
            <Choice on={prefs.textSize === 'large'} onClick={() => change({ textSize: 'large' })}>Grand</Choice>
            <Choice on={prefs.textSize === 'xlarge'} onClick={() => change({ textSize: 'xlarge' })}>Très grand</Choice>
          </div>
        </section>

        <section className="tvs-card">
          <h2><Sparkles size="1em" /> Animations</h2>
          <p>Les réduire rend l'affichage plus calme sur les petites box.</p>
          <div className="tvs-choices">
            <Choice on={!prefs.reduceMotion} onClick={() => change({ reduceMotion: false })}>Normales</Choice>
            <Choice on={prefs.reduceMotion} onClick={() => change({ reduceMotion: true })}>Réduites</Choice>
          </div>
        </section>
      </div>

      <footer className="tvs-foot">
        <button type="button" className="tvh-btn" onClick={reset}>
          <RotateCcw size="1.1em" /> Remettre par défaut
        </button>
        <span>Retour : touche ← de la télécommande</span>
      </footer>
    </div>
  )
}
